
import { computed, defineComponent, reactive, ref } from 'vue';
import useNumbers from '@/composables/useNumbers';
import { TransactionResponse } from '@ethersproject/abstract-provider';
import useTransactions from '@/composables/useTransactions';
import useEthers from '@/composables/useEthers';
import useWeb3 from '@/services/web3/useWeb3';
import { balancerService } from '@/services/balancer/balancer.service';
import { bnum } from '@/lib/utils';
import { TransactionActionInfo } from '@/types/transactions';
import { useI18n } from 'vue-i18n';
import useFlakeApprovalActions from '@/composables/useFlakeApprovalActions';
// @ts-ignore
import TokenFarmInput from '@/components/inputs/TokenInput/TokenFarmInput.vue';
import useBalanceQuery from '@/composables/queries/useBalanceQuery';

/**
 * STATE
 */
const ACTIONTYPE = ['Stake', 'Unstake'];
const ACTIONTITLE = ['FLAKE Balance', 'Withdrawable veFlake Balance'];

export default defineComponent({
  components: {
    TokenFarmInput
  },

  props: {
    changeType: { type: String, default: '' },
    hasBar: { type: Boolean, default: false },
    barName: { type: String, default: '' },
    userLeaveRequestsQuery: { type: Object }
  },

  setup(props) {
    const { getProvider, appNetworkConfig } = useWeb3();
    const { fNum2 } = useNumbers();
    const { t } = useI18n();
    const { addTransaction } = useTransactions();
    const { txListener } = useEthers();
    const flakeBalanceQuery = useBalanceQuery(
      appNetworkConfig.addresses.flake || ''
    );
    const veFlakeBalanceQuery = useBalanceQuery(
      appNetworkConfig.addresses.veFlake || ''
    );
    /**
     * STATE
     */
    const state = reactive({
      navCurIndex: 0,
      customSlippage: '',
      isCustomInput: false,
      inputAmount: '0'
    });

    const flakeBalance = computed(
      () => (flakeBalanceQuery.data.value as string) || '0'
    );
    const veFlakeBalance = computed(
      () => (veFlakeBalanceQuery.data.value as string) || '0'
    );
    const { tokenApprovalActions } = useFlakeApprovalActions(
      [appNetworkConfig.addresses.flake || ''],
      ref([state.inputAmount === '0' ? '1' : state.inputAmount])
    );

    const btnStatus = computed(() => {
      if (state.navCurIndex === 0) {
        return (
          bnum(flakeBalance.value || '0').eq(0) || state.inputAmount === '0'
        );
      } else {
        return (
          bnum(veFlakeBalance.value || '0').eq(0) || state.inputAmount === '0'
        );
      }
    });

    const navBtnColor = computed(() => {
      if (props.changeType === 'stake') {
        return 'custom-black-300';
      }
      return 'custom-black-300';
    });

    const navBtnActiveColor = computed(() => {
      if (props.changeType === 'stake') {
        return 'custom-blue-50';
      } else if (props.changeType === 'boost') {
        return 'custom-green-50';
      }
      return 'custom-green-50';
    });

    const balanceNameColor = computed(() => {
      if (props.changeType === 'stake') {
        return 'text-gray-950';
      }
      return 'text-custom-black-350';
    });

    const actionTitle = computed(() => ACTIONTITLE[state.navCurIndex]);
    const actionTitleValue = computed(() => {
      let data = '';
      switch (state.navCurIndex) {
        case 0:
          data = flakeBalance.value
            ? // @ts-ignore
              bnum(flakeBalance.value || '0')
                .div(Math.pow(10, 18))
                .toString()
            : '0';
          break;
        case 1:
          data = veFlakeBalance.value
            ? // @ts-ignore
              bnum(veFlakeBalance.value || '0')
                .div(Math.pow(10, 18))
                .toString()
            : '0';
          break;
      }
      return data;
    });

    const chooseNavIndex = index => {
      state.navCurIndex = index;
      state.inputAmount = '0';
    };

    const handleInAmountChange = x => {
      state.inputAmount = x;
    };

    async function stakeFlake(): Promise<TransactionResponse> {
      const provider = getProvider();
      try {
        const formatAmount = bnum(state.inputAmount)
          .abs()
          .times(Math.pow(10, 18))
          .toString(16);
        const params = [`0x${formatAmount}`];
        const tx = await balancerService.farmPools.normalFarm.stakeFlake(
          provider,
          params
        );

        addTransaction({
          id: tx.hash,
          type: 'tx',
          action: 'stake',
          summary: 'Stake FLAKE',
          details: {
            name: 'MyStakeFlake'
          }
        });

        txListener(tx, {
          onTxConfirmed: async () => {
            flakeBalanceQuery.refetch.value();
            veFlakeBalanceQuery.refetch.value();
            state.inputAmount = '0';
          },
          onTxFailed: () => {
            console.log('Stake FLAKE failed');
          }
        });

        return tx;
      } catch (e) {
        console.log(e);
        return Promise.reject('Stake FLAKE failed');
      }
    }

    async function withdrawVeFlake(): Promise<TransactionResponse> {
      const provider = getProvider();
      // @ts-ignore
      try {
        const formatAmount = bnum(state.inputAmount)
          .abs()
          .times(Math.pow(10, 18))
          .toString(16);
        const params = [`0x${formatAmount}`];
        const tx = await balancerService.farmPools.normalFarm.leaveApply(
          provider,
          params
        );

        addTransaction({
          id: tx.hash,
          type: 'tx',
          action: 'withdraw',
          summary: 'Unstake veFlake',
          details: {
            name: 'MyFarmPool'
          }
        });

        txListener(tx, {
          onTxConfirmed: async () => {
            veFlakeBalanceQuery.refetch.value();
            state.inputAmount = '0';
            props.userLeaveRequestsQuery?.refetch.value();
          },
          onTxFailed: () => {
            console.log('Unstake failed');
          }
        });

        return tx;
      } catch (e) {
        console.log(e);
        return Promise.reject('Unstake failed');
      }
    }

    const allActions = computed((): TransactionActionInfo[] => [
      {
        label: 'Stake',
        loadingLabel: 'Confirm staking in wallet',
        confirmingLabel: t('confirming'),
        action: () => stakeFlake(),
        stepTooltip: 'Confirm staking into this pool'
      },
      {
        label: 'Unstake',
        loadingLabel: 'Confirm withdrawing in wallet',
        confirmingLabel: t('confirming'),
        action: () => withdrawVeFlake(),
        stepTooltip: 'Confirm withdrawing into this pool'
      }
    ]);

    const actions = computed((): TransactionActionInfo[] => {
      const currentAction = allActions.value.filter(
        (action, index) => state.navCurIndex === index
      );
      return [...tokenApprovalActions, ...currentAction];
    });

    return {
      state,
      navBtnColor,
      navBtnActiveColor,
      balanceNameColor,
      chooseNavIndex,
      fNum2,
      stakeFlake,
      withdrawVeFlake,
      actions,
      bnum,
      handleInAmountChange,
      ACTIONTYPE,
      actionTitle,
      flakeBalance,
      btnStatus,
      actionTitleValue
    };
  }
});
